/** @jsxImportSource react */
import {
  Alert, Autocomplete, Box, Button, Divider, FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { arrayMove } from '@dnd-kit/sortable'
import { FIELD_TYPES, capabilities, defaultParentLink, emptyRollup, newOption, toOptionValue } from './builderModel'
import { findFieldLocation } from './builderReducer'
import { entityLabel, entityLabelPlural, toFormCode } from '../../utils/helpers'
import { useEntityTypes, usePublishedForm } from '../../hooks/useCrmQueries'
import { allFields } from '../records/fieldValues'

const Heading = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 2 }}>
    {children}
  </Typography>
)

const FormSettings = ({ meta, isNew, readOnly, errors, dispatch }) => {
  const set = (patch) => dispatch({ type: 'setMeta', patch })
  return (
    <Stack spacing={2}>
      <TextField
        label="Form name"
        required
        value={meta.name}
        disabled={readOnly}
        error={!!errors.meta.name}
        helperText={errors.meta.name}
        onChange={(e) => {
          const name = e.target.value
          // code follows the name until the form is first saved; after that it is permanent
          set(isNew && !meta.codeTouched ? { name, code: toFormCode(name) } : { name })
        }}
      />
      <TextField
        label="Record type"
        required
        value={meta.entityType}
        disabled={readOnly}
        error={!!errors.meta.entityType}
        helperText={errors.meta.entityType || 'Records of this type are listed together, e.g. LEAD, CONTACT, DEAL'}
        onChange={(e) => set({ entityType: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
      />
      <TextField
        label="Code"
        required
        value={meta.code}
        disabled={readOnly || !isNew}
        error={!!errors.meta.code}
        helperText={errors.meta.code || (isNew ? 'Permanent identifier; cannot change after the first save' : 'Permanent identifier')}
        onChange={(e) => set({ code: e.target.value.toLowerCase(), codeTouched: true })}
        InputProps={{ sx: { fontFamily: 'monospace' } }}
      />
      <TextField label="Description" multiline minRows={2} value={meta.description} disabled={readOnly} onChange={(e) => set({ description: e.target.value })} />
    </Stack>
  )
}

const SectionSettings = ({ section, readOnly, errors, dispatch }) => {
  const set = (patch) => dispatch({ type: 'updateSection', uid: section.uid, patch })
  return (
    <Stack spacing={2}>
      {errors.items[section.uid] && <Alert severity="error">{errors.items[section.uid].join(' · ')}</Alert>}
      <TextField label="Section title" required value={section.title} disabled={readOnly} onChange={(e) => set({ title: e.target.value })} />
      <TextField label="Description" multiline minRows={2} value={section.description} disabled={readOnly} onChange={(e) => set({ description: e.target.value })} />
      <TextField select label="Columns" value={section.columns} disabled={readOnly} onChange={(e) => set({ columns: Number(e.target.value) })} helperText="Layout on wide screens; phones always show one column">
        {[1, 2, 3, 4].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
      </TextField>
    </Stack>
  )
}

const OptionsEditor = ({ options, readOnly, onChange }) => {
  const update = (index, patch) => onChange(options.map((o, i) => {
    if (i !== index) return o
    const next = { ...o, ...patch }
    if (patch.label !== undefined && !o.valueTouched) next.value = toOptionValue(patch.label)
    if (patch.value !== undefined) next.valueTouched = true
    return next
  }))

  return (
    <Box>
      <Stack spacing={1}>
        {options.map((option, index) => (
          <Box key={option.uid} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
            <TextField label="Label" value={option.label} disabled={readOnly} onChange={(e) => update(index, { label: e.target.value })} />
            <TextField label="Value" value={option.value} disabled={readOnly} onChange={(e) => update(index, { value: e.target.value })} InputProps={{ sx: { fontFamily: 'monospace' } }} />
            {!readOnly && (
              <Box sx={{ display: 'flex', flexShrink: 0, pt: 0.5 }}>
                <IconButton disabled={index === 0} onClick={() => onChange(arrayMove(options, index, index - 1))} aria-label="Move option up"><ArrowUpwardIcon fontSize="small" /></IconButton>
                <IconButton disabled={index === options.length - 1} onClick={() => onChange(arrayMove(options, index, index + 1))} aria-label="Move option down"><ArrowDownwardIcon fontSize="small" /></IconButton>
                <IconButton onClick={() => onChange(options.filter((_, i) => i !== index))} aria-label="Remove option"><CloseIcon fontSize="small" /></IconButton>
              </Box>
            )}
          </Box>
        ))}
      </Stack>
      {!readOnly && (
        <Button startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => onChange([...options, newOption(`Option ${options.length + 1}`)])}>
          Add option
        </Button>
      )}
    </Box>
  )
}

const NUMERIC = ['NUMBER', 'DECIMAL', 'CURRENCY', 'ROLLUP']
const FILTERABLE = ['SELECT', 'RADIO', 'CHECKBOX', 'TEXT', 'NUMBER']

/** LOOKUP: whether the linked record is this record's parent, and what that means. */
const ParentLinkSettings = ({ field, meta, readOnly, set }) => {
  const link = field.parentLink
  const tree = field.lookupEntityType && field.lookupEntityType === meta.entityType
  const parentName = field.lookupEntityType ? entityLabel(field.lookupEntityType).toLowerCase() : 'linked record'
  const ownName = meta.entityType ? entityLabelPlural(meta.entityType).toLowerCase() : 'these records'
  return (
    <>
      <FormControlLabel disabled={readOnly}
        control={<Switch checked={!!link} onChange={(e) => set({ parentLink: e.target.checked ? defaultParentLink() : null })} />}
        label={tree ? 'Builds a tree (parent / sub-records)' : `Belongs to the ${parentName} (parent link)`} />
      {!link && (
        <Typography variant="caption" color="text.secondary">
          Turn on when {ownName} are parts of a {parentName}, e.g. line items of a quote{tree ? ', or sub-accounts of an account' : ''}.
        </Typography>
      )}
      {link && (
        <>
          <TextField select label={`When the ${parentName} is deleted`} value={link.onParentDelete} disabled={readOnly}
            onChange={(e) => set({ parentLink: { ...link, onParentDelete: e.target.value } })}>
            <MenuItem value="CASCADE">Delete its {ownName} too</MenuItem>
            <MenuItem value="BLOCK">Don’t allow it while it has {ownName}</MenuItem>
          </TextField>
          {!tree && (
            <TextField select label={`On the ${parentName}’s page, show ${ownName} as`} value={link.display} disabled={readOnly}
              onChange={(e) => set({ parentLink: { ...link, display: e.target.value } })}
              helperText={link.display === 'TABLE' ? 'Rows are added and edited right on the parent’s page' : 'A list with key columns; each opens on its own page'}>
              <MenuItem value="TABLE">Editable table (line items)</MenuItem>
              <MenuItem value="LIST">List</MenuItem>
            </TextField>
          )}
          {tree && (
            <Typography variant="caption" color="text.secondary">
              Lists of {ownName} get a Tree view, record pages show their parents, and a record can’t be moved under its own sub-records.
            </Typography>
          )}
        </>
      )}
    </>
  )
}

/** ROLLUP: count / total of a field over the records that link here. */
const RollupSettings = ({ field, meta, sections, readOnly, set }) => {
  const { entityTypes, forms } = useEntityTypes()
  const r = field.rollup || emptyRollup()
  const update = (patch) => set({ rollup: { ...r, ...patch } })
  const own = r.entityType && r.entityType === meta.entityType
  const code = own ? null : forms.find((f) => f.entityType === r.entityType)?.code
  const { data: childForm } = usePublishedForm(code)
  const childFields = own ? sections.flatMap((s) => s.fields) : childForm ? allFields(childForm) : []
  const links = childFields.filter((f) => f.fieldType === 'LOOKUP' && f.lookupEntityType === meta.entityType)
  const measures = childFields.filter((f) => NUMERIC.includes(f.fieldType) && f.fieldKey !== field.fieldKey)
  const filterable = childFields.filter((f) => FILTERABLE.includes(f.fieldType))
  const filterField = filterable.find((f) => f.fieldKey === r.filterField)
  const types = [...new Set([...entityTypes, ...(meta.entityType ? [meta.entityType] : [])])]

  return (
    <>
      <TextField select label="Records to roll up" value={r.entityType} disabled={readOnly}
        onChange={(e) => update({ entityType: e.target.value, via: '', field: '', filterField: '', filterValue: '', descendants: false })}
        helperText="Published record types; publish the other type first">
        {types.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}{t === meta.entityType ? ' (this type: sub-records)' : ''}</MenuItem>)}
      </TextField>
      {r.entityType && (
        <TextField select label="Linked through" value={links.some((l) => l.fieldKey === r.via) ? r.via : ''} disabled={readOnly}
          onChange={(e) => update({ via: e.target.value })}
          error={links.length === 0}
          helperText={links.length === 0 ? `${entityLabelPlural(r.entityType)} have no link field to ${meta.entityType || 'this type'}` : undefined}>
          {links.map((l) => <MenuItem key={l.fieldKey} value={l.fieldKey}>{l.label}</MenuItem>)}
        </TextField>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField select label="Calculate" value={r.fn} disabled={readOnly} onChange={(e) => update({ fn: e.target.value })}
          sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0 }}>
          <MenuItem value="COUNT">Count</MenuItem>
          <MenuItem value="SUM">Sum</MenuItem>
          <MenuItem value="AVG">Average</MenuItem>
          <MenuItem value="MIN">Lowest</MenuItem>
          <MenuItem value="MAX">Highest</MenuItem>
        </TextField>
        {r.fn !== 'COUNT' && (
          <TextField select label="of" value={measures.some((m) => m.fieldKey === r.field) ? r.field : ''} disabled={readOnly || !r.entityType}
            onChange={(e) => update({ field: e.target.value })}>
            {measures.map((m) => <MenuItem key={m.fieldKey} value={m.fieldKey}>{m.label}</MenuItem>)}
          </TextField>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField select label="Only where (optional)" value={filterField ? r.filterField : ''} disabled={readOnly || !r.entityType}
          onChange={(e) => update({ filterField: e.target.value, filterValue: '' })} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
          <MenuItem value=""><em>All of them</em></MenuItem>
          {filterable.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
        </TextField>
        {filterField && (filterField.options?.length ? (
          <TextField select label="is" value={r.filterValue} disabled={readOnly} onChange={(e) => update({ filterValue: e.target.value })}>
            {filterField.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        ) : filterField.fieldType === 'CHECKBOX' ? (
          <TextField select label="is" value={r.filterValue} disabled={readOnly} onChange={(e) => update({ filterValue: e.target.value })}>
            <MenuItem value="true">Checked</MenuItem>
            <MenuItem value="false">Unchecked</MenuItem>
          </TextField>
        ) : (
          <TextField label="is" value={r.filterValue} disabled={readOnly} onChange={(e) => update({ filterValue: e.target.value })} />
        ))}
      </Box>
      {own && (
        <FormControlLabel disabled={readOnly}
          control={<Switch checked={!!r.descendants} onChange={(e) => update({ descendants: e.target.checked })} />}
          label="Include all levels below (sub-records of sub-records)" />
      )}
      <Typography variant="caption" color="text.secondary">
        Kept up to date automatically whenever a linked record changes. Users can’t edit it, but can filter, sort and chart by it.
      </Typography>
    </>
  )
}

/** "Required when Stage is Lost": another select / radio / checkbox of this form and some of its values. */
const RequiredWhenSettings = ({ field, sections, readOnly, set }) => {
  const rule = field.requiredWhen
  const candidates = sections.flatMap((sec) => sec.fields)
    .filter((f) => f.fieldKey && f.fieldKey !== field.fieldKey && ['SELECT', 'RADIO', 'CHECKBOX'].includes(f.fieldType))
  const on = candidates.find((f) => f.fieldKey === rule?.field)
  const choices = on?.fieldType === 'CHECKBOX'
    ? [{ value: 'true', label: 'Ticked' }, { value: 'false', label: 'Not ticked' }]
    : (on?.options || []).filter((o) => o.value).map((o) => ({ value: o.value, label: o.label || o.value }))
  if (!candidates.length) {
    return <Typography variant="caption" color="text.secondary">Add a select, radio or checkbox field to make this one required on a condition.</Typography>
  }
  return (
    <Stack spacing={1.5}>
      <TextField select size="small" label="Required when" value={rule?.field || ''} disabled={readOnly}
        onChange={(e) => set({ requiredWhen: e.target.value ? { field: e.target.value, values: [] } : null })}>
        <MenuItem value="">Never (optional)</MenuItem>
        {candidates.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label || f.fieldKey}</MenuItem>)}
      </TextField>
      {on && (
        <Autocomplete multiple size="small" disabled={readOnly} options={choices} getOptionLabel={(o) => o.label}
          value={choices.filter((c) => rule.values.includes(c.value))}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          onChange={(_, picked) => set({ requiredWhen: { field: on.fieldKey, values: picked.map((p) => p.value) } })}
          renderInput={(params) => <TextField {...params} label={`is one of`} placeholder="Pick values" />} />
      )}
      {on && (
        <Typography variant="caption" color="text.secondary">
          E.g. a lost reason when Stage is Lost. Checked on every save, including moving a card on the board.
        </Typography>
      )}
    </Stack>
  )
}

const FieldSettings = ({ field, meta, sections, savedKeys, readOnly, errors, fieldTypes, dispatch }) => {
  const set = (patch) => dispatch({ type: 'updateField', uid: field.uid, patch })
  const caps = capabilities(field.fieldType, fieldTypes)
  const keyWasSaved = savedKeys.has(field.fieldKey)
  const isRollup = field.fieldType === 'ROLLUP'

  return (
    <Stack spacing={2}>
      {errors.items[field.uid] && <Alert severity="error">{errors.items[field.uid].join(' · ')}</Alert>}
      <TextField label="Label" required value={field.label} disabled={readOnly} onChange={(e) => set({ label: e.target.value })} autoFocus={!readOnly} />
      <TextField
        label="Key"
        required
        value={field.fieldKey}
        disabled={readOnly}
        onChange={(e) => set({ fieldKey: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
        InputProps={{ sx: { fontFamily: 'monospace' } }}
        helperText={keyWasSaved ? 'Records store values under this key. Renaming it hides existing values.' : 'Records store values under this key'}
      />
      <TextField select label="Type" value={field.fieldType} disabled={readOnly} onChange={(e) => {
        const fieldType = e.target.value
        const next = capabilities(fieldType, fieldTypes)
        set({
          fieldType,
          ...(next.supportsOptions && field.options.length === 0 ? { options: [newOption('Option 1'), newOption('Option 2')] } : {}),
          ...(fieldType === 'ROLLUP' && !field.rollup ? { rollup: emptyRollup() } : {}),
        })
      }}>
        {FIELD_TYPES.map((t) => <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>)}
      </TextField>

      <Box>
        {!isRollup && <FormControlLabel control={<Switch checked={field.required} disabled={readOnly} onChange={(e) => set({ required: e.target.checked })} />} label="Required" />}
        {!caps.supportsLookup && !isRollup && <FormControlLabel control={<Switch checked={field.readOnly} disabled={readOnly} onChange={(e) => set({ readOnly: e.target.checked })} />} label="Read-only" />}
        <FormControlLabel control={<Switch checked={field.hidden} disabled={readOnly} onChange={(e) => set({ hidden: e.target.checked })} />} label="Hidden" />
        {field.readOnly && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Read-only fields always hold their default value; users can’t change them.
          </Typography>
        )}
      </Box>

      {caps.supportsLookup && (
        <>
          <Divider />
          <Heading>Links to</Heading>
          <LookupTargetInput value={field.lookupEntityType} readOnly={readOnly} onChange={(lookupEntityType) => set({ lookupEntityType })} />
          <ParentLinkSettings field={field} meta={meta} readOnly={readOnly} set={set} />
        </>
      )}

      {!isRollup && !field.required && (
        <>
          <Divider />
          <Heading>Rule</Heading>
          <RequiredWhenSettings field={field} sections={sections} readOnly={readOnly} set={set} />
        </>
      )}

      {isRollup && (
        <>
          <Divider />
          <Heading>Calculation</Heading>
          <RollupSettings field={field} meta={meta} sections={sections} readOnly={readOnly} set={set} />
        </>
      )}

      {caps.supportsOptions && (
        <>
          <Divider />
          <Heading>Options</Heading>
          <OptionsEditor options={field.options} readOnly={readOnly} onChange={(options) => set({ options })} />
        </>
      )}

      <Divider />
      <Heading>Display</Heading>
      <TextField label="Placeholder" value={field.placeholder} disabled={readOnly} onChange={(e) => set({ placeholder: e.target.value })} />
      <TextField label="Help text" value={field.helpText} disabled={readOnly} onChange={(e) => set({ helpText: e.target.value })} />
      {!caps.supportsLookup && !isRollup && <DefaultValueInput field={field} readOnly={readOnly} onChange={(defaultValue) => set({ defaultValue })} />}

      {(caps.supportsLength || caps.supportsRange || caps.supportsPattern) && (
        <>
          <Divider />
          <Heading>Validation</Heading>
        </>
      )}
      {caps.supportsLength && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Min length" type="number" value={field.minLength} disabled={readOnly} onChange={(e) => set({ minLength: e.target.value })} inputProps={{ min: 0 }} />
          <TextField label="Max length" type="number" value={field.maxLength} disabled={readOnly} onChange={(e) => set({ maxLength: e.target.value })} inputProps={{ min: 0 }} />
        </Box>
      )}
      {caps.supportsRange && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Min value" type="number" value={field.minValue} disabled={readOnly} onChange={(e) => set({ minValue: e.target.value })} />
          <TextField label="Max value" type="number" value={field.maxValue} disabled={readOnly} onChange={(e) => set({ maxValue: e.target.value })} />
        </Box>
      )}
      {caps.supportsPattern && (
        <>
          <TextField label="Pattern (regex)" value={field.pattern} disabled={readOnly} onChange={(e) => set({ pattern: e.target.value })} InputProps={{ sx: { fontFamily: 'monospace' } }} helperText="The whole value must match, e.g. ^[A-Z]{3}-\d{4}$" />
          {field.pattern && <TextField label="Message when it doesn't match" value={field.patternMessage} disabled={readOnly} onChange={(e) => set({ patternMessage: e.target.value })} />}
        </>
      )}
    </Stack>
  )
}

/** Target record type of a LOOKUP: pick an existing one or type a new one (it may be published later). */
const LookupTargetInput = ({ value, readOnly, onChange }) => {
  const { entityTypes } = useEntityTypes()
  return (
    <Autocomplete
      freeSolo
      disabled={readOnly}
      options={entityTypes}
      getOptionLabel={(t) => t}
      renderOption={(props, t) => <li {...props} key={t}>{entityLabel(t)} <code style={{ marginLeft: 8, opacity: 0.6 }}>{t}</code></li>}
      value={value || null}
      inputValue={value || ''}
      onInputChange={(_, next) => onChange(next.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
      onChange={(_, next) => onChange((next || '').toUpperCase())}
      renderInput={(params) => (
        <TextField {...params} label="Record type" required
          helperText="Records of this type can be picked, e.g. ACCOUNT. Its first text field is shown as the name." />
      )}
    />
  )
}

/** Default value editor shaped by the field type; stored as the backend's string format. */
const DefaultValueInput = ({ field, readOnly, onChange }) => {
  const common = { label: 'Default value', value: field.defaultValue, disabled: readOnly }
  switch (field.fieldType) {
    case 'USER':
      return (
        <TextField select {...common} onChange={(e) => onChange(e.target.value)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          helperText="“Current user” makes whoever creates the record its owner">
          <MenuItem value=""><em>None</em></MenuItem>
          <MenuItem value="$me">Current user</MenuItem>
        </TextField>
      )
    case 'CHECKBOX':
      return (
        <TextField select {...common} onChange={(e) => onChange(e.target.value)}>
          <MenuItem value=""><em>None</em></MenuItem>
          <MenuItem value="true">Checked</MenuItem>
          <MenuItem value="false">Unchecked</MenuItem>
        </TextField>
      )
    case 'SELECT':
    case 'RADIO':
      return (
        <TextField select {...common} onChange={(e) => onChange(e.target.value)}>
          <MenuItem value=""><em>None</em></MenuItem>
          {field.options.filter((o) => o.value).map((o) => <MenuItem key={o.uid} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      )
    case 'MULTI_SELECT': {
      const selected = field.defaultValue ? field.defaultValue.split(',') : []
      return (
        <TextField select {...common} value={selected} SelectProps={{ multiple: true }} onChange={(e) => onChange(e.target.value.join(','))}>
          {field.options.filter((o) => o.value).map((o) => <MenuItem key={o.uid} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      )
    }
    case 'DATE':
      return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} onChange={(e) => onChange(e.target.value)} />
    case 'DATETIME':
      return <TextField {...common} type="datetime-local" InputLabelProps={{ shrink: true }} inputProps={{ step: 1 }} onChange={(e) => onChange(e.target.value)} />
    default:
      return <TextField {...common} onChange={(e) => onChange(e.target.value)} />
  }
}

const SettingsPanel = ({ state, isNew, readOnly, errors, fieldTypes, savedKeys, dispatch }) => {
  const { selected } = state
  let title = 'Form settings'
  let body = <FormSettings meta={state.meta} isNew={isNew} readOnly={readOnly} errors={errors} dispatch={dispatch} />

  if (selected.kind === 'section') {
    const section = state.sections.find((s) => s.uid === selected.uid)
    if (section) {
      title = 'Section'
      body = <SectionSettings section={section} readOnly={readOnly} errors={errors} dispatch={dispatch} />
    }
  } else if (selected.kind === 'field') {
    const location = findFieldLocation(state.sections, selected.uid)
    if (location) {
      const field = location.section.fields[location.index]
      title = 'Field'
      body = <FieldSettings key={field.uid} field={field} meta={state.meta} sections={state.sections} savedKeys={savedKeys} readOnly={readOnly} errors={errors} fieldTypes={fieldTypes} dispatch={dispatch} />
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{title}</Typography>
        {selected.kind !== 'form' && (
          <Tooltip title="Back to form settings">
            <IconButton onClick={() => dispatch({ type: 'select', selected: { kind: 'form' } })} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </Box>
      {body}
    </Paper>
  )
}

export default SettingsPanel
